import { z } from 'zod';
import { ProblemSchema } from './schemas.generated.js';
/**
 * Kode galat yang dapat dikeluarkan server. Setiap kode di sini WAJIB punya entri
 * `error.<KODE>` di kedua katalog `@assist/i18n` — uji katalog memeriksanya.
 *
 * ADR T1-06: server tidak pernah mengirim kalimat. Daftar ini adalah kontrak
 * antara lapis API dan lapis bahasa.
 */
export const ERROR_CODES = [
    'VALIDATION_FAILED',
    'INVALID_CREDENTIALS',
    'ACCOUNT_LOCKED',
    'TENANT_INACTIVE',
    'USER_NOT_ELIGIBLE',
    'UNAUTHENTICATED',
    'SESSION_EXPIRED',
    'SESSION_REVOKED',
    'SESSION_UNKNOWN',
    'FORBIDDEN',
    'NOT_FOUND',
    'ENTITLEMENT_REQUIRED',
    'TRANSITION_NOT_ALLOWED',
    'STATE_CONFLICT',
    'AFTER_PHOTO_REQUIRED',
    'PHOTO_REQUIRED',
    'LOCATION_REQUIRED',
    'CATEGORY_REQUIRED',
    'DESCRIPTION_TOO_LONG',
    'LOCATION_INACTIVE',
    'CODE_BELONGS_TO_ANOTHER_ORGANISATION',
    'OWNER_REQUIRED',
    'OWNER_OUTSIDE_TENANT',
    'OWNER_DISABLED',
    'REASON_REQUIRED',
    'DUE_DATE_IN_PAST',
    'SELF_VERIFICATION_NOT_ALLOWED',
    'ESCALATION_AT_TOP_LEVEL',
    'NOT_ESCALATED',
    'NO_ESCALATION_TRIGGER',
    'INSTRUMENT_VERSION_CONFLICT',
    'INSTRUMENT_VERSION_WITHDRAWN',
    'RATE_LIMITED',
    'INTERNAL',
    'TENANT_NOT_FOUND',
    'CONSULTANT_NOT_ASSIGNED',
    'CANNOT_REVOKE_CURRENT_SESSION',
    'RESET_TOKEN_INVALID',
    'CONTEXT_NOT_BOUND',
    // Persetujuan dan pemberitahuan privasi (UU 27/2022). Setiap kode di bawah punya
    // kalimatnya di KEDUA katalog, dan gerbang teks antarmuka memeriksanya dengan
    // mengenumerasi `new AppError('...')` dari seluruh kode — jadi menambahkan kode
    // di sini tanpa kalimatnya menggagalkan pembangunan.
    'CONSENT_REQUIRED',
    // Pasal 51 — perjanjian pemrosesan data di tingkat organisasi.
    'TENANT_AGREEMENT_PENDING',
    'AGREEMENT_ALREADY_ACCEPTED',
    'NOTICE_ACKNOWLEDGEMENT_REQUIRED',
    'CONSENT_ALREADY_WITHDRAWN',
    // Pengakuan telah diberi tahu (Pasal 21) BUKAN izin, jadi menariknya ditolak —
    // dan ditolak dengan kode yang berbeda dari CONSENT_ALREADY_WITHDRAWN, karena
    // yang perlu dibaca orangnya berbeda: bukan "Anda sudah menariknya" melainkan
    // "ini tidak pernah izin, dan pemrosesannya tidak bergantung padanya".
    'ACKNOWLEDGEMENT_CANNOT_BE_WITHDRAWN',
    'CONSENT_PURPOSE_UNKNOWN',
    'INVITATION_INVALID',
    'PHOTO_METADATA_PENDING',
    // Pengerasan jalur unggah (migrasi 0029). Ketiganya dilempar trigger
    // `attachment_file_usable`, dan ketiganya HARUS punya kodenya sendiri: tanpa itu,
    // "berkas itu milik organisasi lain", "berkas itu belum selesai diunggah", dan
    // "berkas itu belum diverifikasi" menjadi satu 500 INTERNAL yang tidak dapat
    // ditindaklanjuti klien mana pun.
    'ATTACHMENT_FILE_NOT_FOUND',
    'ATTACHMENT_FILE_NOT_USABLE',
    'ATTACHMENT_FILE_NOT_VERIFIED',
    'LEADERBOARD_SCOPE_FORBIDDEN',
    'PROCESSING_BASIS_MISSING',
    /**
     * PERBAIKAN G3 — enam kode yang lahir karena UAT membuktikan jawabannya tidak dapat
     * ditindaklanjuti tanpa mereka.
     *
     * `PHOTO_TOO_LARGE` sudah DIJANJIKAN `openapi.yaml` sebelum ini dan tidak pernah
     * dikirim: `file_field_photo_budget` menjadi `VALIDATION_FAILED` tanpa `field_errors`,
     * pada kegagalan yang paling sering terjadi di seluruh produk (U2-08).
     *
     * `NOT_IMPLEMENTED` menggantikan `404` pada 95 rute manifest yang permukaannya ada
     * dan handlernya belum (U2-03). `404` tidak dapat dibedakan klien dari "objek yang
     * Anda minta tidak ada", sehingga layar yang membacanya menampilkan kondisi kosong
     * alih-alih mengatakan yang sebenarnya.
     *
     * `PASSWORD_TOO_WEAK` adalah konsekuensi keputusan pemilik proyek nomor 1: penguncian
     * akun keras dihapus, jadi kebijakan kata sandi minimum menjadi kontrol yang
     * menggantikannya dan ia harus dapat menjelaskan dirinya kepada orang yang ditolaknya.
     */
    'PHOTO_TOO_LARGE',
    'FILE_TYPE_NOT_ALLOWED',
    'ITEM_CATEGORY_UNKNOWN',
    'NOT_IMPLEMENTED',
    'PASSWORD_TOO_WEAK',
    'USER_ALREADY_ACTIVE',
    /**
     * GELOMBANG LAYAR KONSOL — empat kode yang lahir bersama handler-nya, bukan sesudahnya.
     *
     * Ketiga yang pertama SUDAH TERTULIS di `workflows/daily-huddle.v1.json` sebagai
     * `error_code` guard sejak alur kerja itu diterbitkan — yaitu sudah dijanjikan kepada
     * klien — dan tidak pernah ada di daftar ini. Sebuah guard yang gagal dengan kode di
     * luar daftar tertutup dijawab klien sebagai kalimat cadangan, dan kalimat cadangan
     * pada guard "rapat tidak dapat ditutup karena belum ada satu butir pun yang dibahas"
     * tidak memberi tahu apa pun tentang apa yang harus dilakukan.
     *
     * `QUERY_TOO_SHORT` dijanjikan `api/openapi.yaml` pada `GET /v1/search`
     * ("Kueri < 3 karakter ditolak 422 QUERY_TOO_SHORT") sebelum rutenya punya handler.
     */
    'MEETING_SLOT_TAKEN',
    'NOTHING_DISCUSSED',
    'TIMEBOX_NOT_ELAPSED',
    'QUERY_TOO_SHORT',
    /**
     * GELOMBANG IDENTITAS ORGANISASI — tiga kode, dan ketiganya lahir bersama rutenya.
     *
     * Daftar ini TERTUTUP dengan sengaja: sebuah kode di luarnya dijawab klien dengan
     * kalimat cadangan, dan kalimat cadangan pada "warna yang Anda pilih tidak dapat
     * dibedakan dari halaman" membuat orang menebak warna berikutnya. Ketiganya punya
     * kalimatnya dalam DUA bahasa di `packages/i18n/src/catalog.ts`, dan gerbang teks
     * antarmuka menuntutnya.
     *
     * `ACCENT_CONTRAST_TOO_LOW` membawa `params` — rasio, minimum, dan nama permukaan —
     * karena penolakan tanpa angka menyuruh orang mencoba lagi secara membabi buta.
     */
    'ACCENT_HEX_INVALID',
    'ACCENT_CONTRAST_TOO_LOW',
    'TIMEZONE_NOT_SUPPORTED',
    /**
     * GELOMBANG M15 — LIMA BELAS RUTE KONSOL PLATFORM. Tiga kode, dan ketiganya lahir
     * bersama handler-nya.
     *
     * `PLATFORM_STATE_CONFLICT` BUKAN `STATE_CONFLICT`, dan perbedaannya bukan selera.
     * Kalimat `STATE_CONFLICT` berbunyi "Temuan ini sudah berubah sejak terakhir dilihat
     * perangkat Anda" — kalimat tentang temuan, pada rute yang menangguhkan ORGANISASI.
     * Memakainya kembali berarti seorang admin platform yang menangguhkan organisasi
     * yang sudah tertangguh membaca kalimat tentang temuan, dan kalimat yang salah lebih
     * buruk daripada kode mentah karena ia terlihat benar.
     *
     * `JOB_NOT_RETRYABLE` menjawab tombol `Jalankan ulang` pada job yang manifest
     * umumkan tetapi pekerja latar belum punya kodenya. Tanpa kode ini, satu-satunya
     * bentuk jujur adalah menyembunyikan tombolnya — dan tombol yang hilang tanpa
     * penjelasan menyuruh orang mengira job itu tidak pernah gagal.
     *
     * `AUDIT_EXPORT_TOO_LARGE` menolak ekspor jejak yang melewati batas baris, DENGAN
     * angkanya di `params`. Penolakan tanpa angka menyuruh orang mempersempit rentang
     * secara membabi buta.
     */
    'PLATFORM_STATE_CONFLICT',
    'JOB_NOT_RETRYABLE',
    'AUDIT_EXPORT_TOO_LARGE',
];
export const ErrorCodeSchema = z.enum(ERROR_CODES);
/** Benar bila jawaban server berbentuk Problem. */
export function isProblem(value) {
    return ProblemSchema.safeParse(value).success;
}
//# sourceMappingURL=problem.js.map